/**
 * Knowledge Tree Service for NewME Chat
 * Provides structured conversational flows and responses when Dialogflow is offline
 */

export interface KnowledgeNode {
  id: string;
  keywords: string[];
  response: string;
  followUpQuestions?: string[];
  nextNodes?: string[];
  category:
    | "platform"
    | "self-determination"
    | "technical"
    | "account"
    | "general";
}

export interface ConversationContext {
  currentTopic?: string;
  previousTopics: string[];
  userIntent?: string;
  conversationStage: "initial" | "exploring" | "deep-dive" | "resolution";
}

class KnowledgeTreeService {
  private knowledgeTree: Map<string, KnowledgeNode> = new Map();
  private conversationContext: ConversationContext = {
    previousTopics: [],
    conversationStage: "initial",
  };

  constructor() {
    this.initializeKnowledgeTree();
  }

  private initializeKnowledgeTree() {
    // Bug Report Category
    this.addNode({
      id: "bug-report",
      keywords: [
        "bug",
        "error",
        "problem",
        "issue",
        "not working",
        "broken",
        "mapping issue",
        "trouble with",
        "found a bug",
        "report a bug",
        "report bug",
        "i found a bug",
        "there's a bug",
        "i have a bug",
      ],
      response:
        "🐛 Let's get your mapping back on track. To help you effectively, I need to gather some details step by step.\n\nFirst, what were you trying to do when the bug occurred?",
      followUpQuestions: [
        "I was creating a map",
        "I was trying to save my work",
        "I was logging in",
        "I was browsing the platform",
      ],
      nextNodes: ["bug-step-2", "bug-step-2", "bug-step-2", "bug-step-2"],
      category: "technical",
    });

    // Bug Report Step 2 - Bug Description
    this.addNode({
      id: "bug-step-2",
      keywords: [
        "creating a map",
        "trying to save",
        "logging in",
        "browsing the platform",
        "was trying",
        "was creating",
        "was saving",
        "was browsing",
      ],
      response:
        "Thanks! Now, please describe what went wrong in your own words. What exactly happened when you tried to do that?",
      followUpQuestions: [],
      nextNodes: ["bug-step-2b"],
      category: "technical",
    });

    // Bug Report Step 2b - Error Messages
    this.addNode({
      id: "bug-step-2b",
      keywords: [
        "error",
        "bug",
        "problem",
        "issue",
        "went wrong",
        "happened",
        "not working",
        "broken",
        "crash",
        "freeze",
        "stuck",
        "failed",
        "couldn't",
        "unable",
        "didn't work",
        "stopped working",
        "malfunction",
        "glitch",
        "issue with",
        "trouble with",
      ],
      response:
        "Got it! Did you see any specific error messages or codes? If yes, please share them. If no, just say 'no error message'.",
      followUpQuestions: [],
      nextNodes: ["bug-step-2c"],
      category: "technical",
    });

    // Bug Report Step 2c - Validate Description
    this.addNode({
      id: "bug-step-2c",
      keywords: [
        "no error message",
        "error message",
        "error text",
        "i'll describe",
        "i can copy",
        "javascript error",
        "network error",
        "page not found",
      ],
      response:
        "Thanks for the details! Now, what type of device were you using?",
      followUpQuestions: [
        "Computer (Windows)",
        "Computer (Mac)",
        "Computer (Linux)",
        "Android Phone",
        "iPhone",
        "Android Tablet",
        "iPad",
        "Other device",
      ],
      nextNodes: [
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
      ],
      category: "technical",
    });

    // Bug Report Step 3 - Device Type
    this.addNode({
      id: "bug-step-3",
      keywords: [
        "no error message",
        "error message",
        "error text",
        "i'll describe",
        "i can copy",
        "javascript error",
        "network error",
        "page not found",
      ],
      response: "Got it! Now, what type of device are you using?",
      followUpQuestions: [
        "Computer (Windows)",
        "Computer (Mac)",
        "Computer (Linux)",
        "Android Phone",
        "iPhone",
        "Android Tablet",
        "iPad",
        "Other device",
      ],
      nextNodes: [
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
        "bug-step-3b",
      ],
      category: "technical",
    });

    // Bug Report Step 3b - Browser
    this.addNode({
      id: "bug-step-3b",
      keywords: [
        "computer (windows)",
        "computer (mac)",
        "computer (linux)",
        "android phone",
        "iphone",
        "android tablet",
        "ipad",
        "other device",
        "computer",
        "desktop",
        "pc",
        "laptop",
        "mobile",
        "phone",
        "tablet",
        "android",
        "iphone",
        "ipad",
        "windows",
        "mac",
        "linux",
        "other",
      ],
      response: "Perfect! Now, which browser were you using?",
      followUpQuestions: [
        "Chrome",
        "Firefox",
        "Safari",
        "Edge",
        "Opera",
        "Brave",
        "Internet Explorer",
        "Other browser",
      ],
      nextNodes: [
        "bug-step-4",
        "bug-step-4",
        "bug-step-4",
        "bug-step-4",
        "bug-step-4",
        "bug-step-4",
        "bug-step-4",
        "bug-step-4",
      ],
      category: "technical",
    });

    // Bug Report Step 4 - Reproducibility
    this.addNode({
      id: "bug-step-4",
      keywords: [
        "chrome",
        "firefox",
        "safari",
        "edge",
        "opera",
        "brave",
        "internet explorer",
        "other browser",
        "google chrome",
        "mozilla firefox",
        "microsoft edge",
        "browser",
        "web browser",
      ],
      response:
        "Perfect! Last question: can you reproduce this issue consistently? In other words, does it happen every time you try the same action?",
      followUpQuestions: [
        "Yes, it happens every time",
        "No, it's intermittent",
        "I haven't tried again",
        "It's random",
      ],
      nextNodes: [
        "bug-complete",
        "bug-complete",
        "bug-complete",
        "bug-complete",
      ],
      category: "technical",
    });

    // Bug Report Complete
    this.addNode({
      id: "bug-complete",
      keywords: [
        "happens every time",
        "it's intermittent",
        "haven't tried again",
        "it's random",
        "yes",
        "no",
        "consistently",
        "intermittent",
        "every time",
        "random",
        "tried again",
        "intermittent",
        "consistently",
      ],
      response:
        "Excellent! 🦅 I have all the details I need for your bug report. When you're done, I'll save everything and our team will investigate this issue. You'll receive updates via email when we have progress to share.\n\nIs there anything else I can help you with?",
      followUpQuestions: [
        "Report another bug",
        "Request a feature",
        "General question",
        "No, that's all",
      ],
      nextNodes: [
        "bug-report",
        "feature-request",
        "general-question",
        "conversation-end",
      ],
      category: "technical",
    });

    // Feature Request Category
    this.addNode({
      id: "feature-request",
      keywords: [
        "feature",
        "request",
        "suggestion",
        "improvement",
        "new feature idea",
        "add",
        "enhancement",
        "have a feature request",
      ],
      response:
        "Brilliant! 🦅 New features help us better serve the self-determination movement. Let me gather some details to make your request effective.\n\nFirst, what feature would you like to see added to NewME?",
      followUpQuestions: [
        "Map creation improvements",
        "Community features",
        "User interface enhancements",
        "Other platform features",
      ],
      nextNodes: [
        "feature-step-2",
        "feature-step-2",
        "feature-step-2",
        "feature-step-2",
      ],
      category: "platform",
    });

    // Feature Request Step 2 - Community Benefit
    this.addNode({
      id: "feature-step-2",
      keywords: [
        "map creation improvements",
        "community features",
        "user interface enhancements",
        "other platform features",
        "map creation",
        "community",
        "interface",
        "platform",
        "drawing",
        "sharing",
        "ui",
        "features",
      ],
      response:
        "Great idea! Now, how would this feature help the NewME community? What problem would it solve or what would it make easier?",
      followUpQuestions: [
        "It would make mapping easier",
        "It would improve collaboration",
        "It would help with organization",
        "It would enhance user experience",
      ],
      nextNodes: [
        "feature-step-3",
        "feature-step-3",
        "feature-step-3",
        "feature-step-3",
      ],
      category: "platform",
    });

    // Feature Request Step 3 - Use Cases
    this.addNode({
      id: "feature-step-3",
      keywords: [
        "make mapping easier",
        "improve collaboration",
        "help with organization",
        "enhance user experience",
        "easier",
        "collaboration",
        "organization",
        "experience",
        "better",
        "improve",
      ],
      response:
        "Excellent! Can you describe a specific use case? For example, 'When I'm creating a map, I would use this feature to...' or 'This would help when users want to...'",
      followUpQuestions: [
        "I'll describe a specific scenario",
        "It would help when creating maps",
        "It would improve the sharing process",
        "It would make navigation better",
      ],
      nextNodes: [
        "feature-step-4",
        "feature-step-4",
        "feature-step-4",
        "feature-step-4",
      ],
      category: "platform",
    });

    // Feature Request Step 4 - Examples
    this.addNode({
      id: "feature-step-4",
      keywords: [
        "describe a specific scenario",
        "help when creating maps",
        "improve the sharing process",
        "make navigation better",
        "scenario",
        "creating maps",
        "sharing",
        "navigation",
        "specific",
        "example",
      ],
      response:
        "Perfect! Last question: do you have any examples of similar features from other platforms that work well? This helps our team understand what you're envisioning.",
      followUpQuestions: [
        "Yes, I can give examples",
        "No specific examples",
        "I'll describe what I'm thinking",
        "Similar to other mapping tools",
      ],
      nextNodes: [
        "feature-complete",
        "feature-complete",
        "feature-complete",
        "feature-complete",
      ],
      category: "platform",
    });

    // Feature Request Complete
    this.addNode({
      id: "feature-complete",
      keywords: [
        "yes, i can give examples",
        "no specific examples",
        "i'll describe what i'm thinking",
        "similar to other mapping tools",
        "examples",
        "no examples",
        "describe",
        "similar",
        "yes",
        "no",
      ],
      response:
        "Fantastic! 🦅 I have all the details I need for your feature request. When you're done, I'll save everything and our team will review it for future updates. We'll keep you posted on the progress via email!\n\nIs there anything else I can help you with?",
      followUpQuestions: [
        "Report a bug",
        "Request another feature",
        "General question",
        "No, that's all",
      ],
      nextNodes: [
        "bug-report",
        "feature-request",
        "general-question",
        "conversation-end",
      ],
      category: "platform",
    });

    // General Question Category
    this.addNode({
      id: "general-question",
      keywords: [
        "question",
        "help",
        "support",
        "how to",
        "platform help",
        "general question",
        "information",
        "about",
      ],
      response:
        "Please describe your question or what you need help with, and I'll make sure our team gets back to you!",
      followUpQuestions: [],
      nextNodes: ["general-complete"],
      category: "general",
    });

    // General Question Complete
    this.addNode({
      id: "general-complete",
      keywords: [
        "question",
        "help",
        "support",
        "how to",
        "platform help",
        "general question",
        "information",
        "about",
        "inquiry",
        "ask",
        "wondering",
        "curious",
        "need help",
        "want to know",
      ],
      response:
        "Perfect! 🦅 I have your question. When you're done, I'll save everything and our team will review it. We'll get back to you with a helpful response via email!\n\nIs there anything else I can help you with?",
      followUpQuestions: [
        "Report a bug",
        "Request a feature",
        "No, that's all",
      ],
      nextNodes: ["bug-report", "feature-request", "conversation-end"],
      category: "general",
    });

    // Default fallback
    this.addNode({
      id: "default",
      keywords: ["default"],
      response:
        "Soar! 🦅 I'm here to help with NewME. What can I assist you with today?",
      followUpQuestions: [
        "Report a bug",
        "Request a feature",
        "General question",
      ],
      nextNodes: ["bug-report", "feature-request", "general-question"],
      category: "general",
    });

    // Conversation End
    this.addNode({
      id: "conversation-end",
      keywords: [
        "no, that's all",
        "that's all",
        "no",
        "nothing else",
        "all done",
        "finished",
        "thanks",
        "thank you",
        "no that's all",
        "no, thats all",
        "no thats all",
      ],
      response:
        "Perfect! 🦅 Thank you for reaching out to NewME. Your inquiry has been saved to our database and our team will review it. Your input helps us improve the platform for the self-determination movement. Feel free to come back anytime you need assistance!\n\nKeep mapping for a better Middle East! 🗺️",
      followUpQuestions: [],
      nextNodes: [],
      category: "general",
    });
  }

  private addNode(node: KnowledgeNode) {
    this.knowledgeTree.set(node.id, node);
  }

  /**
   * Process user message and return appropriate response
   */
  processMessage(userMessage: string): {
    response: string;
    followUpQuestions?: string[];
    nextNodes?: string[];
    context: ConversationContext;
  } {
    const lowerMessage = userMessage.toLowerCase();

    // Special handling for bug description step
    if (this.conversationContext.currentTopic === "bug-step-2") {
      if (!this.isMeaningfulBugDescription(userMessage)) {
        return {
          response:
            "Could you tell me a bit more about what happened? Even a short description helps! For example: 'The page froze when I clicked save' or 'I got an error message after entering my password'.",
          followUpQuestions: [],
          nextNodes: ["bug-step-2"],
          context: this.conversationContext,
        };
      }
    }

    // Find the best matching node
    const matchedNode = this.findBestMatch(lowerMessage);

    // Update conversation context
    this.updateContext(matchedNode);

    return {
      response: matchedNode.response,
      followUpQuestions: matchedNode.followUpQuestions,
      nextNodes: matchedNode.nextNodes,
      context: this.conversationContext,
    };
  }

  private findBestMatch(message: string): KnowledgeNode {
    let bestMatch = this.knowledgeTree.get("default")!;
    let bestScore = 0;

    this.knowledgeTree.forEach((node, id) => {
      if (id === "default") return;

      const score = this.calculateMatchScore(message, node);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = node;
      }
    });

    return bestMatch;
  }

  private calculateMatchScore(message: string, node: KnowledgeNode): number {
    let score = 0;
    const lowerMessage = message.toLowerCase();

    // Check keyword matches with more flexible matching
    for (const keyword of node.keywords) {
      const lowerKeyword = keyword.toLowerCase();
      if (lowerMessage.includes(lowerKeyword)) {
        score += lowerKeyword.length; // Longer keywords get higher scores
      } else {
        // Check for partial matches (words within keywords)
        const keywordWords = lowerKeyword.split(" ");
        let partialMatch = 0;
        for (const word of keywordWords) {
          if (lowerMessage.includes(word) && word.length > 2) {
            partialMatch += word.length;
          }
        }
        if (partialMatch > 0) {
          score += partialMatch * 0.5; // Partial matches get lower scores
        }
      }
    }

    // Boost score if it matches current context
    if (this.conversationContext.currentTopic === node.id) {
      score += 10;
    }

    // Boost score for category continuity
    if (this.conversationContext.previousTopics.length > 0) {
      const lastTopic = this.knowledgeTree.get(
        this.conversationContext.previousTopics[
          this.conversationContext.previousTopics.length - 1
        ]
      );
      if (lastTopic && lastTopic.category === node.category) {
        score += 5;
      }
    }

    // Boost score if this is the expected next step
    if (this.conversationContext.previousTopics.length > 0) {
      const lastTopic = this.knowledgeTree.get(
        this.conversationContext.previousTopics[
          this.conversationContext.previousTopics.length - 1
        ]
      );
      if (
        lastTopic &&
        lastTopic.nextNodes &&
        lastTopic.nextNodes.includes(node.id)
      ) {
        score += 20; // High boost for expected next step
      }
    }

    // Special handling for conversation end - boost score for ending phrases
    if (node.id === "conversation-end") {
      const endingPhrases = [
        "no, that's all",
        "that's all",
        "no that's all",
        "no, thats all",
        "no thats all",
        "nothing else",
        "all done",
        "finished",
        "thanks",
        "thank you",
      ];

      for (const phrase of endingPhrases) {
        if (lowerMessage.includes(phrase.toLowerCase())) {
          score += 15; // High boost for clear ending signals
          break;
        }
      }
    }

    // Special handling for bug report - require more specific context
    if (node.id === "bug-report") {
      // Only match bug report if there are clear bug indicators
      const bugIndicators = [
        "bug",
        "error",
        "problem",
        "issue",
        "not working",
        "broken",
        "mapping issue",
        "trouble with",
        "found a bug",
        "crash",
        "freeze",
      ];
      const hasBugIndicator = bugIndicators.some((indicator) =>
        lowerMessage.includes(indicator)
      );

      // If no clear bug indicators, significantly reduce score
      if (!hasBugIndicator) {
        score *= 0.1; // Reduce score by 90% if no bug indicators
      }
    }

    // Special handling for general questions - boost score for common questions
    if (node.category === "general" || node.category === "platform") {
      const generalQuestionIndicators = [
        "what is",
        "how does",
        "how to",
        "tell me about",
        "explain",
        "about newme",
        "platform work",
        "getting started",
      ];
      const hasGeneralIndicator = generalQuestionIndicators.some((indicator) =>
        lowerMessage.includes(indicator)
      );

      if (hasGeneralIndicator) {
        score += 10; // Boost score for general question indicators
      }
    }

    return score;
  }

  private updateContext(matchedNode: KnowledgeNode) {
    this.conversationContext.previousTopics.push(matchedNode.id);
    this.conversationContext.currentTopic = matchedNode.id;

    // Keep only last 5 topics to prevent context overflow
    if (this.conversationContext.previousTopics.length > 5) {
      this.conversationContext.previousTopics =
        this.conversationContext.previousTopics.slice(-5);
    }

    // Update conversation stage
    if (
      matchedNode.category === "self-determination" ||
      matchedNode.category === "platform"
    ) {
      this.conversationContext.conversationStage = "exploring";
    } else if (
      matchedNode.category === "technical" ||
      matchedNode.category === "account"
    ) {
      this.conversationContext.conversationStage = "resolution";
    }
  }

  /**
   * Get follow-up questions for a specific node
   */
  getFollowUpQuestions(nodeId: string): string[] {
    const node = this.knowledgeTree.get(nodeId);
    return node?.followUpQuestions || [];
  }

  /**
   * Reset conversation context
   */
  resetContext() {
    this.conversationContext = {
      previousTopics: [],
      conversationStage: "initial",
    };
  }

  /**
   * Get conversation context
   */
  getContext(): ConversationContext {
    return { ...this.conversationContext };
  }

  /**
   * Get information about where messages are saved
   */
  getMessageSavingInfo(): string {
    return "Your messages are automatically saved to our secure database. Our development team reviews all inquiries and will follow up via email when appropriate. Your privacy is protected and your data is never shared with third parties.";
  }

  /**
   * Check if a message indicates conversation end
   */
  isConversationEnding(message: string): boolean {
    // Decode HTML entities first
    const decodedMessage = message
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");

    const lowerMessage = decodedMessage.toLowerCase();
    const endingPhrases = [
      "no, that's all",
      "that's all",
      "no that's all",
      "no, thats all",
      "no thats all",
      "nothing else",
      "all done",
      "finished",
      "thanks",
      "thank you",
      "bye",
      "goodbye",
    ];

    return endingPhrases.some((phrase) => lowerMessage.includes(phrase));
  }

  /**
   * Check if a bug description is meaningful (not too short or generic)
   */
  isMeaningfulBugDescription(message: string): boolean {
    const lowerMessage = message.toLowerCase().trim();

    // Too short descriptions (reduced from 10 to 5 characters)
    if (lowerMessage.length < 5) {
      return false;
    }

    // Only reject very generic single-word responses
    const veryGenericResponses = [
      "yes",
      "no",
      "ok",
      "okay",
      "sure",
      "fine",
      "good",
      "bad",
      "error",
      "bug",
      "problem",
      "issue",
    ];

    // Check if it's just a very generic single-word response
    if (veryGenericResponses.some((response) => lowerMessage === response)) {
      return false;
    }

    // Accept most descriptions that are longer than 5 characters
    // This is much more lenient and user-friendly
    return true;
  }
}

export const knowledgeTreeService = new KnowledgeTreeService();
